import { useState } from 'react';
import { Document } from '../components/document-list-vault/types';
import { NWClient } from '../../../client/NWClient';
import { DocumentSigningModel } from '../../../models/document.signing.model';
import { ROUTES } from '../../../common/constants/routes';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { SIGNING_DATA } from '../types';
import { showMessage } from '../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { hideOverlay } from '../../../slices/show-progress-overlay/showProgressOverlay';
import { selectBusinessProfileDetails } from '../../../slices/business-profile/businessProfileSlice';
import { sanitizeRedirectUrl } from '../../../common/helpers/sanitize';
import { useDocumensoRedirect } from '../../../common/hooks/useDocumensoRedirect';

type SigningData = {
  user: number;
  category?: string;
  document_upload_html?: number;
  document_contract?: number;
  document_upload?: number;
  profile_id?: number;
};

export const useDocumentSigning = () => {
  const currentUser = useAppSelector(selectUser);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const businessProfileDetails = useAppSelector(selectBusinessProfileDetails);
  const path = location.pathname;
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [document, setDocument] = useState<Document>(null);
  const [openAssignPersonDialog, setOpenAssignPersonDialog] = useState<boolean>(false);

  const { redirectToDocumenso, isLoading: False } = useDocumensoRedirect();

  const getProfileLabelsProp = (category: string) => {
    if (category === 'employment') {
      return 'employees';
    } else if (category === 'business') {
      return 'business_profile_contacts';
    } else {
      return category;
    }
  };

  const handleSign = (data: SIGNING_DATA) => {
    setPendingStatus(true);
    setDialogOpen(false);
    NWClient.post('document-sign', data, true)
      .then((res: DocumentSigningModel) => {
        console.log(document);
        redirectToDocumenso(
          `${ROUTES.DOCUMENTS}/${res.signing_doc_id}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}${
            path === ROUTES.DOCUMENT_VAULT ? `&activeTab=${document?.category}` : ''
          }`
        );
        //navigate(document_sign_url(String(res.signing_doc_id)));
      })
      .catch((error) => {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.userNotAssociatedWithCompany')
        ) {
          navigate(
            `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
          );
        } else {
          toast.error(error.message ? error.message : t('messages.errorOccurred'));
        }
      })
      .finally(() => {
        setPendingStatus(false);
        dispatch(hideOverlay());
      });
  };

  const handleSignLatestVersion = (doc: Document, latestHTMLId?: number, profileId?: number) => {
    const data: SigningData = {
      user: currentUser.id,
      document_upload_html:
        'document_upload_html' in doc
          ? latestHTMLId
            ? latestHTMLId
            : Math.max(...doc.document_upload_html)
          : null,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    if (doc.category === 'suppliers' || doc.category === 'business') {
      data.profile_id = profileId;
      data.category = doc.category;
    } else if (
      doc &&
      (((doc.category === 'employment' || doc.category === 'customers') &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        doc[getProfileLabelsProp(doc.category)].length > 0) ||
        doc.category === 'business')
    ) {
      data.profile_id =
        doc.category === 'business'
          ? businessProfileDetails?.id
          : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            doc[getProfileLabelsProp(doc.category)][0];
      data.category = doc.category;
    }
    handleSign(data);
  };

  const handleSignOriginalDocument = (doc: Document, profileId?: number) => {
    const data: SigningData = {
      user: currentUser.id,
      [doc.contract_generation ? 'document_contract' : 'document_upload']: doc.id,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    if (doc.category === 'suppliers' || doc.category === 'business') {
      data.profile_id = profileId;
      data.category = doc.category;
    } else if (
      doc &&
      (((doc.category === 'employment' || doc.category === 'customers') &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        doc[getProfileLabelsProp(doc.category)].length > 0) ||
        doc.category === 'business')
    ) {
      data.profile_id =
        doc.category === 'business'
          ? businessProfileDetails?.id
          : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            doc[getProfileLabelsProp(doc.category)][0];
      data.category = doc.category;
    }
    handleSign(data);
  };

  return {
    dialogOpen,
    setDialogOpen,
    document,
    setDocument,
    pendingStatus,
    setPendingStatus,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  };
};
